// Which layers of a shot can be generated, and what to say about their price. Pure, so the rules are tested
// without a browser:
//   - keyframe and video are made per shot and priced by the shot estimate;
//   - dialogue is made per scene, one take per character, priced by the scene estimate for the speaker of the
//     shot's own lines;
//   - music and sound effects need an audio model that offers them. No adapter does yet, so those buttons are
//     off, with one quiet line, and never show a price. When a model with a matching mode shows up in
//     GET /models, they switch on by themselves.
import type { Api } from "./api.ts";
import type { DialogueLine, Estimate, Layer, ModelInfo, Tier } from "./types.ts";

/** The modes of an audio model that make each layer. `tts` is dialogue and is not listed here. */
export const AUDIO_MODES: Readonly<Partial<Record<Layer, readonly string[]>>> = {
  music: ["music", "text2music"],
  sfx: ["sfx", "text2sfx"],
};

/** The one quiet line shown when nothing can make the layer. */
export const NO_MODEL_LINE: Readonly<Partial<Record<Layer, string>>> = {
  music: "No music model connected yet",
  sfx: "No sound-effects model connected yet",
};

/** What the list of models says: still loading, could not be read, or the models themselves. */
export type ModelsState = "loading" | "unknown" | readonly ModelInfo[];

export type Availability =
  | { state: "ok" }
  /** the models are still being asked; the button waits, with no line */
  | { state: "checking" }
  /** no model offers this layer */
  | { state: "none"; line: string };

/** Does a model offer this layer? An audio model whose modes include the layer's mode. */
export function modelOffers(m: ModelInfo, layer: Layer): boolean {
  const modes = AUDIO_MODES[layer];
  if (!modes) return true;
  return m.enabled !== false && m.kind === "audio" && m.modes.some((x) => modes.includes(x));
}

/**
 * Can this layer be generated? Keyframe, video and dialogue are always offered (their own price check tells
 * when no model fits). Music and sound effects need a matching audio model; if the models could not be read
 * the button stays on, because we do not know that there is none.
 */
export function layerAvailability(layer: Layer, models: ModelsState): Availability {
  if (!AUDIO_MODES[layer]) return { state: "ok" };
  if (models === "loading") return { state: "checking" };
  if (models === "unknown") return { state: "ok" };
  if (models.some((m) => modelOffers(m, layer))) return { state: "ok" };
  return { state: "none", line: NO_MODEL_LINE[layer] ?? "No model connected yet" };
}

/** The sound a finished video take can get, in this order: a voice for the shot's lines, sound effects, music. */
export const TAKE_AUDIO: readonly Layer[] = ["dialogue", "sfx", "music"];

/** The modes of an audio model that voice dialogue. */
const VOICE_MODES: readonly string[] = ["tts"];

/**
 * The sound a video take offers as actions (Add voice, Add sound, Add music): only what a connected audio model
 * can make. Nothing while the models are loading or could not be read: an action is never shown on a guess.
 */
export function takeAudio(models: ModelsState): Layer[] {
  if (typeof models === "string") return [];
  return TAKE_AUDIO.filter((layer) =>
    models.some((m) =>
      layer === "dialogue"
        ? m.enabled !== false && m.kind === "audio" && m.modes.some((x) => VOICE_MODES.includes(x))
        : modelOffers(m, layer),
    ),
  );
}

/** What to price, and with which endpoint. */
export type EstimateSubject =
  | { type: "shot"; id: string }
  | { type: "scene"; id: string; characterElementId?: string };

/** Layers made for the whole scene (one dialogue take per character); the rest are made per shot. */
export const SCENE_LAYERS: ReadonlySet<Layer> = new Set(["dialogue", "music"]);

/**
 * The thing to estimate for a layer: the shot, or the scene the shot sits in (with the speaker of the shot's
 * lines). Null when the layer is made per scene and the shot is in none.
 */
export function estimateSubject(
  layer: Layer,
  shotId: string,
  sceneId: string | undefined,
  speakerId?: string,
): EstimateSubject | null {
  if (!SCENE_LAYERS.has(layer)) return { type: "shot", id: shotId };
  if (!sceneId) return null;
  return { type: "scene", id: sceneId, ...(speakerId ? { characterElementId: speakerId } : {}) };
}

/**
 * Who speaks the shot's dialogue: the character of the shot's first line (by position). The scene's other
 * speakers are not this shot's. Undefined when the shot has no spoken line; the server then takes the
 * scene's first speaker, and the price is for that.
 */
export function dialogueSpeaker(lines: readonly DialogueLine[], shotId: string): string | undefined {
  const own = lines
    .filter((l) => l.shot_id === shotId && l.character_element_id)
    .sort((a, b) => a.position - b.position);
  return own[0]?.character_element_id ?? undefined;
}

/** The scene has nothing to speak, so there is nothing to generate or price. */
export const noDialogue = (lines: readonly DialogueLine[]): boolean => lines.length === 0;

export interface EstimateRequest {
  tier: Tier;
  provider?: string;
  modelKey?: string;
}

/** Ask the right endpoint for the price of a layer. */
export function requestEstimate(
  api: Pick<Api, "estimate" | "estimateScene">,
  ws: string,
  subject: EstimateSubject,
  layer: Layer,
  req: EstimateRequest,
): Promise<Estimate> {
  if (subject.type === "shot") return api.estimate(ws, subject.id, { kind: layer, ...req });
  return api.estimateScene(ws, subject.id, {
    kind: layer === "music" ? "music" : "dialogue",
    ...req,
    ...(subject.characterElementId ? { characterElementId: subject.characterElementId } : {}),
  });
}

/** A stable key for one estimate, so a changed input refetches and an old answer is dropped. */
export function estimateKey(subject: EstimateSubject | null, layer: Layer, req: EstimateRequest): string {
  if (!subject) return "";
  const who = subject.type === "scene" ? `${subject.id}|${subject.characterElementId ?? ""}` : subject.id;
  return `${subject.type}|${who}|${layer}|${req.tier}|${req.provider ?? ""}|${req.modelKey ?? ""}`;
}

/** Refusals that mean "this cannot be made", not "the price check failed". */
export function cannotBeMade(code: string | null | undefined): boolean {
  return code === "no_capable_model" || code === "no_dialogue" || code === "unsupported_kind";
}
